import { IMS_CERTIFICATION_URL, POK_FACTS, POK_INSTITUTIONS_LABEL, POK_NAME } from '../constants.js';
import { slugs } from '../hooks/slugs.js';

export const POK_URL = 'https://www.pok.tech';
export const POK_LOGO_URL = 'https://www.pok.tech/images/_POK_Logo_primary.webp';

const POK_LOGO = { '@type': 'ImageObject', url: POK_LOGO_URL, width: 460, height: 152 };

export const POK_ORGANIZATION_ID = `${POK_URL}/#organization`;

/** Third-party review-platform profile URLs. Single source of truth — imported by
 * pokJsonLd's POK_SAME_AS + POK_RATING_SOURCES, by Home's PlataformsIn, and by
 * any compare-page link to G2. Change the URL here and it propagates everywhere. */
export const G2_REVIEWS_URL = 'https://www.g2.com/products/pok-proof-of-knowledge/reviews';
export const CAPTERRA_REVIEWS_URL = 'https://www.capterra.com/p/10031485/POK/reviews/';
export const GETAPP_REVIEWS_URL = 'https://www.getapp.com/healthcare-pharmaceuticals-software/a/pok/';
export const SOFTWARE_ADVICE_REVIEWS_URL = 'https://www.softwareadvice.com/product/530253-POK/reviews/';
export const SOURCEFORGE_REVIEWS_URL = 'https://sourceforge.net/software/product/POK/';

/**
 * Canonical sameAs list for POK (Q138638424). Used both inside POK_ORGANIZATION
 * and standalone where a flat sameAs array is preferable to a full @graph.
 */
export const POK_SAME_AS = [
    'https://www.linkedin.com/company/pok-proof-of-knowledge/',
    'https://x.com/POK_tech',
    'https://www.youtube.com/@POKProofofKnowledge',
    G2_REVIEWS_URL,
    CAPTERRA_REVIEWS_URL,
    GETAPP_REVIEWS_URL,
    SOFTWARE_ADVICE_REVIEWS_URL,
    SOURCEFORGE_REVIEWS_URL,
    'https://www.instagram.com/proofofknowledge/',
    'https://www.crunchbase.com/organization/pok-f811',
    IMS_CERTIFICATION_URL,
    'https://edtechindex.org/product/ultid/P93E-48FF-B63C-D4A3-EA/',
    'https://app.dealroom.co/companies/pok_proof_of_knowledge',
    'https://www.wikidata.org/wiki/Q138638424',
];

export const POK_IDENTIFIERS = [
    {
        '@type': 'PropertyValue',
        propertyID: 'Wikidata',
        value: 'Q138638424',
        url: 'https://www.wikidata.org/wiki/Q138638424',
    },
    {
        '@type': 'PropertyValue',
        propertyID: 'Crunchbase',
        value: 'pok-f811',
        url: 'https://www.crunchbase.com/organization/pok-f811',
    },
];

/**
 * Full Organization schema for POK. Single source of truth — used by Home (in
 * a @graph) and emitted standalone by other top-level pages so every page tells
 * answer engines the same entity story (Wikidata QID, sameAs, awards, founder).
 */
const POK_ORGANIZATION = {
    '@type': 'Organization',
    '@id': POK_ORGANIZATION_ID,
    name: POK_NAME,
    legalName: 'Amelay Corporation SA',
    alternateName: ['POK', 'POK - Verifiable Credentials Platform', 'POK - Digital Credentials Platform'],
    url: POK_URL,
    logo: POK_LOGO,
    description: `${POK_NAME} is a global platform for issuing, managing, and verifying digital credentials. Open Badges 3.0 certified, with free and blockchain-verified credential tiers. Used by ${POK_INSTITUTIONS_LABEL}+ institutions in ${POK_FACTS.countries} countries across Latin America, North America, Europe, and Asia. The only platform with a 100% free unlimited plan.`,
    foundingDate: '2023',
    founder: {
        '@type': 'Person',
        '@id': `${POK_URL}/en/digital-credentials/guido-grinbaum#person`,
        name: 'Guido Grinbaum',
        url: `${POK_URL}/en/digital-credentials/guido-grinbaum`,
        sameAs: ['https://www.linkedin.com/in/guidogrinbaum', 'https://x.com/edugrin'],
    },
    contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'contact@pok.tech',
    },
    address: {
        '@type': 'PostalAddress',
        streetAddress: 'Luis Bonavida 1294',
        addressLocality: 'Montevideo',
        addressCountry: 'UY',
    },
    numberOfEmployees: {
        '@type': 'QuantitativeValue',
        minValue: 10,
        maxValue: 50,
    },
    identifier: POK_IDENTIFIERS,
    areaServed: [
        { '@type': 'Continent', name: 'North America' },
        { '@type': 'Continent', name: 'South America' },
        { '@type': 'Continent', name: 'Central America' },
        { '@type': 'Continent', name: 'Europe' },
        { '@type': 'Continent', name: 'Asia' },
        { '@type': 'Country', name: 'US' },
    ],
    sameAs: POK_SAME_AS,
    award: [
        '1EdTech Certified Platform',
        '1EdTech Open Badges 3.0 Certified',
        '1EdTech CLR 2.0 Certified',
        'HolonIQ Latin America EdTech 100 2024',
        'HolonIQ Latin America EdTech 100 2025',
        'HolonIQ Latin America EdTech 100 2026',
        'European Learning Model (ELM) Compatible',
        'SourceForge Top Performer Fall 2026',
    ],
    knowsAbout: [
        'Digital Credentials',
        'Open Badge 3.0',
        'W3C Verifiable Credentials',
        'Blockchain Verification',
        'Digital Badges',
        'Microcredentials',
    ],
};

/** Standalone emission of the organization entity. Every top-level route emits this once; other
 * schemas on the page point at it by @id (which is what POK_AUTHOR and POK_PUBLISHER resolve to),
 * so a single emission grounds every dependent claim. */
export const POK_ORGANIZATION_SCHEMA = { '@context': 'https://schema.org', ...POK_ORGANIZATION };

/**
 * Reference form for use inside other schemas (author / publisher slots). The
 * full Organization payload is emitted once per page via POK_ORGANIZATION; this
 * short form lets dependent schemas point at it by @id without duplication.
 */
export const POK_AUTHOR = { '@id': POK_ORGANIZATION_ID };

export const POK_PUBLISHER = { '@id': POK_ORGANIZATION_ID };

/**
 * Aggregate rating across third-party review platforms. All sources 5.0 / 5
 * as of 2026-05-28. Per Google's structured-data guidelines, any page
 * emitting this schema must also display a visible attribution to the source
 * — handled by <RatingBadge /> rendered inside FooterContainer site-wide.
 */
export const POK_AGGREGATE_RATING = {
    '@type': 'AggregateRating',
    ratingValue: 5,
    bestRating: 5,
    worstRating: 1,
    reviewCount: 65,
};

/** Trust-badge metadata for the visible attribution. Source of truth for both the schema and the UI. */
export const POK_RATING_SOURCES = [
    { name: 'G2', url: G2_REVIEWS_URL, count: 38, rating: 5 },
    { name: 'Capterra', url: CAPTERRA_REVIEWS_URL, count: 9, rating: 5 },
    { name: 'GetApp', url: GETAPP_REVIEWS_URL, count: 9, rating: 5 },
    { name: 'Software Advice', url: SOFTWARE_ADVICE_REVIEWS_URL, count: 9, rating: 5 },
];

const POK_PROVIDER = { '@id': POK_ORGANIZATION_ID };

const POK_APP_IDENTIFIER = [
    {
        '@type': 'PropertyValue',
        propertyID: 'ULTID',
        value: 'P93E-48FF-B63C-D4A3-EA',
        url: 'https://edtechindex.org/product/ultid/P93E-48FF-B63C-D4A3-EA/',
    },
];

const POK_APP_DESCRIPTION = `${POK_NAME} is a platform for issuing verifiable digital credentials and microcredentials. Certified by 1EdTech for Open Badges 3.0 and CLR 2.0. ISO 27001 and SOC 2 certified. Free unlimited plan.`;

const POK_APP_OFFERS = {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    // Required by Google's Software App rich result; lowPrice alone does not satisfy it.
    price: '0',
    lowPrice: '0',
    highPrice: '3000',
    offerCount: 9,
    offers: [
        {
            '@type': 'Offer',
            name: 'Web Based Free',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            description: '100% free plan with unlimited digital credential issuance under Open Badges 3.0.',
        },
        {
            '@type': 'Offer',
            name: 'Blockchain Verify Basic',
            price: '3000',
            priceCurrency: 'USD',
            description:
                'Annual license: everything in the free web plan plus blockchain registration and cryptographic verification, for up to 50,000 credentials per year.',
            eligibleQuantity: { '@type': 'QuantitativeValue', maxValue: 50000, unitText: 'credential per year' },
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'Blockchain Verify Premium',
            price: '0.30',
            priceCurrency: 'USD',
            description:
                'Per credential: blockchain registration and cryptographic verification plus every NFT premium feature except employability. No minimum, no setup fee.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'credential' },
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 1,000',
            price: '1.50',
            priceCurrency: 'USD',
            description: '$1.50 per certificate. Pack of 1,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 1000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 5,000',
            price: '1.25',
            priceCurrency: 'USD',
            description: '$1.25 per certificate. Pack of 5,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 5000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 10,000',
            price: '1.10',
            priceCurrency: 'USD',
            description: '$1.10 per certificate. Pack of 10,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 10000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 20,000',
            price: '1.00',
            priceCurrency: 'USD',
            description: '$1.00 per certificate. Pack of 20,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 20000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 50,000',
            price: '0.90',
            priceCurrency: 'USD',
            description: '$0.90 per certificate. Pack of 50,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 50000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
        {
            '@type': 'Offer',
            name: 'NFT Ownership – Pack of 100,000',
            price: '0.80',
            priceCurrency: 'USD',
            description: '$0.80 per certificate. Pack of 100,000 NFT credentials.',
            eligibleQuantity: { '@type': 'QuantitativeValue', value: 100000, unitText: 'credential' },
            priceValidUntil: '2027-03-31',
            availability: 'https://schema.org/InStock',
        },
    ],
};

/** The site-wide entity graph. Every page that talks about one of these entities defines the whole
 * node from these constants, under the same @id, so each page reads on its own and all of them
 * describe the same entity. A page may add its own properties on top (a `featureList`), never
 * change the shared ones. */
export const POK_WEBSITE = {
    '@type': 'WebSite',
    '@id': `${POK_URL}/#website`,
    name: POK_NAME,
    alternateName: ['POK', 'POK - Verifiable Credentials Platform', 'POK - Proof of Knowledge'],
    url: POK_URL,
    inLanguage: ['en', 'es', 'pt'],
    publisher: POK_PUBLISHER,
};

export const POK_SOFTWARE = {
    '@type': 'SoftwareApplication',
    '@id': `${POK_URL}/#software`,
    name: POK_NAME,
    url: POK_URL,
    image: POK_LOGO_URL,
    description: POK_APP_DESCRIPTION,
    operatingSystem: 'Web',
    applicationCategory: 'EducationalApplication',
    aggregateRating: POK_AGGREGATE_RATING,
    offers: POK_APP_OFFERS,
    provider: POK_PROVIDER,
    identifier: POK_APP_IDENTIFIER,
};

const productApplication = ({ key, id, name, description, applicationCategory }) => ({
    '@type': 'SoftwareApplication',
    '@id': `${POK_URL}/#${id}`,
    name,
    url: `${POK_URL}/en/${slugs[key].en}`,
    description,
    operatingSystem: 'Web',
    applicationCategory,
    provider: POK_PROVIDER,
});

const POK_SKILLS_GRAPHS = productApplication({
    key: 'skillsGraphs',
    id: 'skills-graphs',
    name: 'POK Skills Graphs',
    description: `POK Skills Graphs, from ${POK_NAME}, maps your curriculum to the skills each learning path develops and gives every student a Skills Graph.`,
    applicationCategory: 'EducationalApplication',
});

const POK_SKILLS_INTELLIGENCE = productApplication({
    key: 'skillsIntelligence',
    id: 'skills-intelligence',
    name: 'POK Skills Intelligence',
    description: `POK Skills Intelligence, from ${POK_NAME}, measures skill gaps by role and by person and connects them to your university's training.`,
    applicationCategory: 'BusinessApplication',
});

export const POK_PRODUCT_APPLICATIONS = {
    skillsGraphs: POK_SKILLS_GRAPHS,
    skillsIntelligence: POK_SKILLS_INTELLIGENCE,
};
